import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api.model';
import { DeliveryStatusEnum, IDeliveryNote } from '../models/delivery-note.model';

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root',
})
export class DeliveryNoteService {
  private apiUrl = `${environment.apiUrl}/delivery-note`;

  constructor(private http: HttpClient) { }

  /**
   * Get paginated delivery notes with search, sorting, and status filter
   */
  getDeliveryNotes(
    page: number = 1,
    pageSize: number = 10,
    search: string = '',
    sortField: string = 'createdAt',
    sortDirection: string = 'desc',
    statusFilter: string = ''
  ): Observable<ApiResponse<PaginatedResponse<IDeliveryNote>>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    if (statusFilter) {
      params = params.set('statusFilter', statusFilter);
    }

    return this.http.get<ApiResponse<PaginatedResponse<IDeliveryNote>>>(`${this.apiUrl}`, { params });
  }

  /**
   * Update delivery note status (quick status change)
   */
  updateDeliveryNoteStatus(id: string, status: string): Observable<ApiResponse<IDeliveryNote>> {
    return this.http.patch<ApiResponse<IDeliveryNote>>(`${this.apiUrl}/${id}/status`, { status });
  }

  /**
   * Get a specific delivery note by ID
   */
  getDeliveryNoteById(id: string): Observable<ApiResponse<IDeliveryNote>> {
    return this.http.get<ApiResponse<IDeliveryNote>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get delivery notes by order ID
   */
  getDeliveryNotesByOrderId(orderId: string): Observable<ApiResponse<IDeliveryNote[]>> {
    return this.http.get<ApiResponse<IDeliveryNote[]>>(`${this.apiUrl}/order/${orderId}`);
  }

  /**
   * Create a new delivery note
   */
  createDeliveryNote(deliveryNote: Partial<IDeliveryNote>): Observable<ApiResponse<IDeliveryNote>> {
    return this.http.post<ApiResponse<IDeliveryNote>>(this.apiUrl, deliveryNote);
  }

  /**
   * Update delivery note
   */
  updateDeliveryNote(id: string, deliveryNote: Partial<IDeliveryNote>): Observable<ApiResponse<IDeliveryNote>> {
    return this.http.put<ApiResponse<IDeliveryNote>>(`${this.apiUrl}/${id}`, deliveryNote).pipe(
      catchError(error => {
        console.error(`Error updating delivery note ${id}:`, error);
        return throwError(() => new Error('Failed to update delivery note'));
      })
    );
  }

  /**
   * Check/uncheck a delivery note position
   */
  checkPosition(deliveryNoteId: string, positionId: string, checked: boolean, checkedBy: string): Observable<ApiResponse<IDeliveryNote>> {
    return this.http.patch<ApiResponse<IDeliveryNote>>(`${this.apiUrl}/${deliveryNoteId}/positions/${positionId}/check`, {
      checked,
      checkedBy,
    });
  }

  /**
   * Export delivery note as PDF
   */
  public exportPdf(id: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/export-pdf/${id}`, {
      responseType: 'blob',
    });
  }

  /**
   * Delete a delivery note
   */
  deleteDeliveryNote(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  public unlockDeliveryNote(id: string): Observable<ApiResponse<IDeliveryNote>> {
    return this.http.patch<ApiResponse<IDeliveryNote>>(`${this.apiUrl}/${id}/unlock`, {});
  }

  /**
   * Get human-readable status name
   */
  getStatusName(status: DeliveryStatusEnum): string {
    switch (status) {
      case DeliveryStatusEnum.DELIVERYNOTEQSCREATED:
        return 'QS - Lieferzettel erstellt';
      case DeliveryStatusEnum.READYFORDELIVERY:
        return 'Versandbereit';
      case DeliveryStatusEnum.DELIVERYNOTECREATED:
        return 'Lieferschein erstellt';
      case DeliveryStatusEnum.DELIVERED:
        return 'Ausgeliefert';
      default:
        return status;
    }
  }

  /**
   * Get CSS class for status badge styling
   */
  getStatusClass(status: DeliveryStatusEnum): string {
    switch (status) {
      case DeliveryStatusEnum.DELIVERYNOTEQSCREATED:
        return 'bg-yellow-400/15 text-yellow-400';
      case DeliveryStatusEnum.READYFORDELIVERY:
        return 'bg-blue-400/15 text-blue-400';
      case DeliveryStatusEnum.DELIVERYNOTECREATED:
        return 'bg-gray-400/15 text-text-secondary';
      case DeliveryStatusEnum.DELIVERED:
        return 'bg-emerald-400/15 text-emerald-400';
      default:
        return 'bg-gray-400/15 text-gray-400';
    }
  }

  /**
   * Get the next status in the workflow
   */
  getNextStatus(currentStatus: DeliveryStatusEnum): DeliveryStatusEnum | null {
    switch (currentStatus) {
      case DeliveryStatusEnum.DELIVERYNOTEQSCREATED:
        return DeliveryStatusEnum.READYFORDELIVERY;
      case DeliveryStatusEnum.READYFORDELIVERY:
        return DeliveryStatusEnum.DELIVERYNOTECREATED;
      case DeliveryStatusEnum.DELIVERYNOTECREATED:
        return DeliveryStatusEnum.DELIVERED;
      case DeliveryStatusEnum.DELIVERED:
        return null;
      default:
        return null;
    }
  }
}
